export const ADMIN_DOMAIN = "thesimpleintel.com";

/** Platform (Simple AI / The Simple Intel) logo - always shown alongside company logo when present */
export const PLATFORM_LOGO = "/assets/logo1.png";

export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://wuwzzufmfqiqejinnoic.supabase.co';

export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Ind1d3p6dWZtZnFpcWVqaW5ub2ljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjkxNjc4NzYsImV4cCI6MjA4NDc0Mzg3Nn0.lWiUZfHFCZN8FgI4SzqBsA0dNa-C3gptj1FPC5wNpi4';

/** Sandbox Data API branch (optional — Admin Data API sandbox tab). */
export const SANDBOX_SUPABASE_URL =
  import.meta.env.VITE_SANDBOX_SUPABASE_URL || 'https://dbzqvrodfwiywnsnondw.supabase.co';

export const SANDBOX_SUPABASE_ANON_KEY =
  import.meta.env.VITE_SANDBOX_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRienF2cm9kZndpeXduc25vbmR3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODcwMDE5NjEsImV4cCI6MjEwMjU3Nzk2MX0.Svx70EOZI5CEi9FwHNKZ0VgQtX88RZE48JiHEDoNovM';

export const SANDBOX_CONFIGURED = Boolean(SANDBOX_SUPABASE_URL && SANDBOX_SUPABASE_ANON_KEY);

export const LOGO_OPTIONS = {
  LOGO1: 'logo1.png',
  LOGO2: 'logo2.png',
} as const;

/** Designation options for signup (procurement/supply chain roles). "Other" stores free text in designation_other. */
export const SIGNUP_DESIGNATION_OPTIONS = [
  'Chief Procurement Officer (CPO)',
  'Procurement Director/Manager',
  'Procurement Analyst',
  'Category Manager',
  'Sourcing Specialist',
  'Buyer/Purchasing Agent',
  'Contract Manager',
  'Supplier Relationship Manager',
  'Procurement Specialist',
  'Purchasing Coordinator/Clerk',
  'Chief Supply Chain Officer (CSCO)',
  'Chief Operating Officer (COO)',
  'Other',
] as const;

// Logo upload constants
export const LOGO_UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 2 * 1024 * 1024, // 2MB
  ALLOWED_TYPES: ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'],
  STORAGE_BUCKET: 'logos',
} as const;

// Special reports upload (per-supplier documents: PDF, Word, PowerPoint, etc.)
export const SPECIAL_REPORTS_UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 25 * 1024 * 1024, // 25MB
  ALLOWED_TYPES: [
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  ],
  STORAGE_BUCKET: 'special-reports',
} as const;

/** Optional Word/PDF attached to Day 1 supplier risk alerts (exact client document). */
export const SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 25 * 1024 * 1024, // 25MB
  ALLOWED_TYPES: [
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  ],
  STORAGE_BUCKET: 'supplier-risk-alert-docs',
} as const;

// AI Provider Constants
export type AIProvider = 'openai' | 'anthropic' | 'google' | 'perplexity' | 'other';

export interface AIModel {
  id: string;
  name: string;
  description?: string;
  max_tokens?: number;
  context_window?: number;
}

export interface AIDefaultConfig {
  temperature: number;
  max_tokens: number;
  top_p: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  top_k?: number; // For Google Gemini
  [key: string]: any;
}

export interface AIProviderInfo {
  id: AIProvider;
  name: string;
  description: string;
  defaultModel: string;
  models: AIModel[];
  defaultConfig: AIDefaultConfig;
  defaultRateLimits: {
    requests_per_minute?: number;
    tokens_per_day?: number;
    requests_per_day?: number;
  };
}

export const AI_PROVIDERS: Record<AIProvider, AIProviderInfo> = {
  openai: {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT-6 Sol, Astra, Luna, GPT-4o, and other OpenAI models',
    defaultModel: 'gpt-6.1-sol',
    models: [
      { id: 'gpt-6.1-sol', name: 'GPT-6.1 Sol', description: 'Near-Astra performance; recommended for Risk analyst', max_tokens: 128000, context_window: 1050000 },
      { id: 'gpt-6-astra', name: 'GPT-6 Astra', description: 'Most capable GPT-6 flagship', max_tokens: 128000, context_window: 1050000 },
      { id: 'gpt-6-luna', name: 'GPT-6 Luna', description: 'Efficient GPT-6 for high-volume tasks', max_tokens: 128000, context_window: 1050000 },
      { id: 'gpt-4o', name: 'GPT-4o', description: 'Previous multimodal flagship', max_tokens: 16384, context_window: 128000 },
      { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Faster and cheaper GPT-4o', max_tokens: 16384, context_window: 128000 },
      { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', description: 'GPT-4 with extended context', max_tokens: 4096, context_window: 128000 },
      { id: 'gpt-4', name: 'GPT-4', description: 'Original GPT-4 model', max_tokens: 8192, context_window: 8192 },
      { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', description: 'Fast and cost-effective', max_tokens: 4096, context_window: 16385 },
    ],
    defaultConfig: {
      temperature: 1.0,
      max_tokens: 2048,
      top_p: 1.0,
      frequency_penalty: 0.0,
      presence_penalty: 0.0,
    },
    defaultRateLimits: {
      requests_per_minute: 60,
      tokens_per_day: 1000000,
      requests_per_day: 10000,
    },
  },
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic (Claude)',
    description: 'Claude 3.5 Sonnet, Claude 3.5 Haiku, Claude 3 Opus, and Claude 3 Sonnet',
    defaultModel: 'claude-3-5-sonnet-latest',
    models: [
      { id: 'claude-3-5-sonnet-latest', name: 'Claude 3.5 Sonnet', description: 'Latest balanced model (recommended)', max_tokens: 8192, context_window: 200000 },
      { id: 'claude-3-5-haiku-latest', name: 'Claude 3.5 Haiku', description: 'Latest fast model', max_tokens: 8192, context_window: 200000 },
      { id: 'claude-3-opus-20240229', name: 'Claude 3 Opus', description: 'Most powerful Claude 3', max_tokens: 4096, context_window: 200000 },
      { id: 'claude-3-sonnet-20240229', name: 'Claude 3 Sonnet', description: 'Balanced Claude 3', max_tokens: 4096, context_window: 200000 },
      { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku', description: 'Fast Claude 3', max_tokens: 4096, context_window: 200000 },
    ],
    defaultConfig: {
      temperature: 1.0,
      max_tokens: 4096,
      top_p: 1.0,
    },
    defaultRateLimits: {
      requests_per_minute: 50,
      tokens_per_day: 1000000,
      requests_per_day: 10000,
    },
  },
  google: {
    id: 'google',
    name: 'Google (Gemini)',
    description: 'Gemini 3 Pro, Gemini 3 Flash, and Gemini 1.5 models',
    defaultModel: 'gemini-3-pro-preview',
    models: [
      { id: 'gemini-3-pro-preview', name: 'Gemini 3 Pro', description: 'Latest most capable model (recommended)', max_tokens: 8192, context_window: 1000000 },
      { id: 'gemini-3-flash-preview', name: 'Gemini 3 Flash', description: 'Latest balanced speed/performance', max_tokens: 8192, context_window: 1000000 },
      { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', description: 'Legacy Pro model (deprecated for new projects)', max_tokens: 8192, context_window: 1000000 },
      { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', description: 'Legacy Flash model (deprecated for new projects)', max_tokens: 8192, context_window: 1000000 },
    ],
    defaultConfig: {
      temperature: 1.0,
      max_tokens: 2048,
      top_p: 0.95,
      top_k: 40,
    },
    defaultRateLimits: {
      requests_per_minute: 60,
      tokens_per_day: 1000000,
      requests_per_day: 10000,
    },
  },
  perplexity: {
    id: 'perplexity',
    name: 'Perplexity',
    description: 'Perplexity online models with real-time web search',
    defaultModel: 'pplx-70b-online',
    models: [
      { id: 'pplx-70b-online', name: 'Perplexity 70B Online', description: 'Most capable with web search (recommended)', max_tokens: 4096, context_window: 4096 },
      { id: 'pplx-7b-online', name: 'Perplexity 7B Online', description: 'Fast with web search', max_tokens: 4096, context_window: 4096 },
      { id: 'pplx-70b-chat', name: 'Perplexity 70B Chat', description: 'Conversation-optimized 70B', max_tokens: 4096, context_window: 4096 },
      { id: 'pplx-7b-chat', name: 'Perplexity 7B Chat', description: 'Conversation-optimized 7B', max_tokens: 4096, context_window: 4096 },
      { id: 'sonar', name: 'Sonar', description: 'Search-optimized model', max_tokens: 4096, context_window: 4096 },
      { id: 'sonar-pro', name: 'Sonar Pro', description: 'Advanced search model', max_tokens: 4096, context_window: 4096 },
    ],
    defaultConfig: {
      temperature: 0.7,
      max_tokens: 2048,
      top_p: 0.9,
    },
    defaultRateLimits: {
      requests_per_minute: 20,
      tokens_per_day: 500000,
      requests_per_day: 5000,
    },
  },
  other: {
    id: 'other',
    name: 'Other',
    description: 'Custom AI provider configuration',
    defaultModel: '',
    models: [],
    defaultConfig: {
      temperature: 1.0,
      max_tokens: 2048,
      top_p: 1.0,
    },
    defaultRateLimits: {
      requests_per_minute: 60,
      tokens_per_day: 1000000,
      requests_per_day: 10000,
    },
  },
};

export const AI_PROVIDER_LIST = Object.values(AI_PROVIDERS) as AIProviderInfo[];
